import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  IconButton,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { useTranslation } from 'react-i18next'
import Button from '../Button/Button'
import { CircularProgress } from '@material-ui/core'
import { ERequestUrl } from '../../redux/helpers/requests'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      borderRadius: 16,
      padding: theme.spacing(4),
      maxWidth: 420,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(2),
    },
    title: {
      fontWeight: 700,
    },
    body: {
      marginBottom: theme.spacing(2),
      color: theme.palette.common?.black,
    },
    expirationDate: {
      fontWeight: 700,
      marginBottom: theme.spacing(3),
    },
    errorText: {
      color: theme.palette.error.main,
      marginBottom: theme.spacing(2),
    },
    confirmButton: {
      width: '100%',
      position: 'relative',
      minHeight: 45,
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
    },
    cancelButton: {
      width: '100%',
      textTransform: 'none',
      fontWeight: 600,
    },
  })
)

type Props = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  loading: boolean
  reservationDays: number
  error?: boolean | number
}

/** Adds `days` business days to `startDate`, skipping weekends and the given holiday dates. */
function addBusinessDays(
  startDate: Date,
  days: number,
  holidays: Set<string>
): Date {
  const result = new Date(startDate)
  let added = 0
  while (added < days) {
    result.setDate(result.getDate() + 1)
    const dayOfWeek = result.getDay()
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
    const dateStr = result.toISOString().split('T')[0]
    const isHoliday = holidays.has(dateStr)
    if (!isWeekend && !isHoliday) {
      added++
    }
  }
  return result
}

const ReservationConfirmModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  reservationDays,
  error,
}: Props) => {
  const classes = useStyles()
  const { t, i18n } = useTranslation()

  const [holidays, setHolidays] = useState<Set<string>>(new Set())

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const currentYear = new Date().getFullYear()
        const [resNow, resNext] = await Promise.all([
          fetch(`${ERequestUrl.apiGouvFr}/${currentYear}.json`),
          fetch(`${ERequestUrl.apiGouvFr}/${currentYear + 1}.json`),
        ])
        const [dataNow, dataNext] = await Promise.all([
          resNow.json(),
          resNext.json(),
        ])
        const allHolidays = new Set<string>([
          ...Object.keys(dataNow),
          ...Object.keys(dataNext),
        ])
        setHolidays(allHolidays)
      } catch (error) {
        console.error('Error fetching public holidays:', error)
      }
    }
    fetchHolidays()
  }, [])

  const expirationDate = useMemo(() => {
    const date = addBusinessDays(new Date(), reservationDays, holidays)
    return new Intl.DateTimeFormat(i18n.language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }, [reservationDays, i18n.language, holidays])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.paper }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          {t('texts:reservations:confirm_title')}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <Typography variant="body2" className={classes.body}>
        {t('texts:reservations:confirm_body', { days: reservationDays })}
      </Typography>

      {expirationDate && (
        <Typography variant="body2" className={classes.expirationDate}>
          {t('texts:reservations:confirm_expiration')}
          {' : '}
          {expirationDate}
        </Typography>
      )}

      {error && (
        <Typography variant="body2" className={classes.errorText}>
          {error === 409
            ? t('texts:reservations:confirm_error_conflict')
            : t('texts:reservations:confirm_error')}
        </Typography>
      )}

      <Button
        className={classes.confirmButton}
        color="primary"
        variant="contained"
        onClick={onConfirm}
        disabled={loading}
        style={{ position: 'relative' }}
      >
        {loading ? (
          <CircularProgress size={'1.5em'} color="inherit" />
        ) : (
          t('texts:reservations:confirm_cta')
        )}
      </Button>

      <Button className={classes.cancelButton} onClick={onClose}>
        {t('texts:reservations:confirm_cancel')}
      </Button>
    </Dialog>
  )
}

export default ReservationConfirmModal
